/**
 * PO 2026-02 · Product Owner QA Access Reset entry.
 *
 * Purpose: give Gabe (and any physical QA operator) a single supported
 * URL that intentionally CLEARS the currently-cached JobClose Preview
 * session and returns to the normal Login page — no Incognito, no
 * DevTools, no manual localStorage editing, no cookie surgery.
 *
 * Bounded scope:
 *   - Clears ONLY the JobClose Preview auth session keys owned by
 *     AuthContext (`jc_token`, `jc_user`, `jc.levi.session_id`) and the
 *     small set of `jc:levi:*` sessionStorage flags the Levi surface
 *     writes on login. Everything else the user has in their browser
 *     is left alone.
 *   - Does NOT introduce a second authentication system, does NOT
 *     bypass `/api/auth/login`, does NOT touch production.
 *   - After the wipe, navigates to `/login` where the existing
 *     login form takes over. The QA operator picks the account and
 *     signs in as normal.
 *
 * Sticky diagnostic marker: writes ``jc:levi:diag=1`` into
 * localStorage before redirect so `LeviDiagnosticsBadge` and the
 * `?leviDiag=1` sticky-aware surfaces remain available on the next
 * authenticated session without the operator having to append the
 * query string again.
 */
import { useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";

const _AUTH_KEYS = ["jc_token", "jc_user", "jc.levi.session_id"];
const _SESSION_KEYS = ["jc:levi:fresh_login", "jc:levi:manual_mode"];

export default function QaLogin() {
  const navigate = useNavigate();
  const [params] = useSearchParams();

  useEffect(() => {
    // Best-effort wipe of ONLY the JobClose Preview auth session.
    try {
      for (const k of _AUTH_KEYS) {
        try { window.localStorage.removeItem(k); } catch { /* noop */ }
      }
      for (const k of _SESSION_KEYS) {
        try { window.sessionStorage.removeItem(k); } catch { /* noop */ }
      }
      // PO 2026-02 · QA sticky is now OPT-IN. `/qa-login` alone
      // CLEARS any prior `jc:levi:diag` so contractor sessions never
      // inherit the internal QA panel. Explicit `/qa-login?diag=1`
      // still arms the sticky for QA operators. `/qa-login?diag=0`
      // remains a redundant disarm (same effect as the default).
      //
      // Note (PO 2026-02): In Preview the badge was previously ALSO
      // driven by `REACT_APP_LEVI_VOICE_DIAG_ENABLED=true`; that
      // env flag has been removed from every non-QA slot so the
      // build-time re-armer is gone. The sticky + `?leviDiag=1`
      // query param remain the ONLY paths to the internal QA
      // surface.
      const diagOn = String(params.get("diag") || "").toLowerCase() === "1";
      try {
        if (diagOn) {
          window.localStorage.setItem("jc:levi:diag", "1");
        } else {
          window.localStorage.removeItem("jc:levi:diag");
        }
      } catch { /* private mode / disabled storage */ }
    } catch { /* extremely defensive */ }
    // Wipe is synchronous (removeItem returns before the next line).
    // Navigate immediately in the same tick so AuthContext re-mounts
    // against fresh empty storage without a timing race.
    navigate("/login?fresh=1", { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div
      data-testid="qa-login-clearing"
      className="min-h-screen flex items-center justify-center bg-white"
    >
      <div className="text-center">
        <div
          className="text-sm font-semibold tracking-wide uppercase"
          style={{ color: "var(--jc-blue, #2563eb)" }}
        >
          QA session reset
        </div>
        <div
          className="mt-2 text-lg font-medium"
          style={{ color: "var(--jc-text, #111)" }}
        >
          Clearing this Preview session…
        </div>
        <div
          className="mt-1 text-sm"
          style={{ color: "var(--jc-text-soft, #64748b)" }}
        >
          You&rsquo;ll land on the login screen in a moment.
        </div>
      </div>
    </div>
  );
}
